/**
 * @file DataTable.tsx
 * @brief Reusable, server-paginated data table built on TanStack Table.
 *
 * @details
 * - Server-side pagination & sorting: the parent passes `pagination`,
 *   `sortBy/sortDir`, and the change handlers, so this component never
 *   knows about the underlying API.
 * - Pluggable empty / loading / error states.
 * - Row-level click handler (e.g., "open detail").
 * - Accessible header buttons for sortable columns.
 *
 * The intent: any feature that needs a table imports this and just
 * declares `columns: ColumnDef[]`.
 *
 * @dependencies
 * - @tanstack/react-table
 * - components/ui/Skeleton, components/feedback/EmptyState
 *
 * @references
 * - WEBPANEL/src/features/users/UsersPage.tsx (first consumer)
 *
 * @author DES
 * @updated 23-08-2026 — column meta.className + tableClassName (overflow)
 */

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, RotateCw } from "lucide-react";
import {
  cloneElement,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { groupFooterKind, type TableRowGroup } from "@/components/data/groupRowsBy";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/Button";
import { EmptyState, type EmptyStateShape } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { cn } from "@/shared/theme/cn";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    /** Applied to both `<th>` and `<td>` for this column. */
    className?: string;
  }
}

function columnClassName(columnDef: {
  meta?: { className?: string };
}): string | undefined {
  return columnDef.meta?.className;
}

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------
export interface PaginationState {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export type SortDirection = "asc" | "desc";
export type GroupNestedStyle = "plain" | "timeline";

type TimelinePosition = "only" | "first" | "middle" | "last";

/**
 * The rail's x for a depth. Starts at 0 (was 28) so a top-level group row
 * has the same space on the left as on the right (DES 01-10-2026: "group
 * list ki left right spacing barabar nahin hai"). Rail, chevron and content
 * all derive from this, so they stay lined up.
 */
function timelineRailLeft(depth: number) {
  return depth * 24;
}

function timelineContentPad(depth: number, withTrailing = false) {
  return timelineRailLeft(depth) + (withTrailing ? 42 : 20);
}

function timelinePosition(index: number, total: number): TimelinePosition {
  if (total <= 1) return "only";
  if (index === 0) return "first";
  if (index === total - 1) return "last";
  return "middle";
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  isFetching?: boolean;
  error?: unknown;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  /** The empty state's skeleton card, like the list's own rows. */
  emptyShape?: EmptyStateShape;

  pagination?: PaginationState;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;

  sortBy?: string;
  sortDir?: SortDirection;
  onSortChange?: (sortBy: string, sortDir: SortDirection) => void;
  sortableColumns?: string[];

  onRowClick?: (row: TData) => void;
  rowKey: (row: TData) => string;

  /** When set, rows render under collapsible employee/user group headers. */
  groups?: TableRowGroup<TData>[];
  groupItemLabel?: string;
  /** Leaf groups show this many rows first; use Load more for the rest. */
  groupRowsInitial?: number;
  groupRowsLoadMore?: number;
  /** When true, every group stays expanded (e.g. while a search filter is active). */
  expandAllGroups?: boolean;
  /** Told when a group opens or closes (server-backed groups fetch on open). */
  onGroupExpandChange?: (key: string, expanded: boolean) => void;
  /**
   * Controlled open groups (e.g. kept in the URL so Back restores them).
   * When set, the table does not keep its own; toggles go through
   * `onGroupExpandChange`.
   */
  expandedGroupKeys?: string[];
  /** Server-backed groups (`group.total` set): fetch the next batch. */
  onGroupLoadMore?: (key: string) => void;
  /** Visual treatment for rows nested under a group header. */
  groupNestedStyle?: GroupNestedStyle;
  /**
   * Optional class for the timeline node (when `groupNestedStyle="timeline"`).
   * Use to encode row status / severity on the spine dots.
   */
  getTimelineMarkerClassName?: (row: TData) => string | undefined;
  /**
   * Optional control rendered after the timeline node, on the branch toward
   * row content (e.g. voice mic). Spine node stays centered on the rail.
   */
  renderTimelineMarkerTrailing?: (row: TData) => ReactNode;
  /** Row-hover tooltip body (shown for the whole `<tr>`, not a single cell). */
  getRowTooltip?: (row: TData) => ReactNode;
  /**
   * `card` (default) — rounded bordered surface for classic list pages.
   * `cell` — flat flush table for `GridBlock` / `GridCell` nesting.
   */
  variant?: "card" | "cell";
  /** Extra classes on the `<table>` (e.g. `table-fixed` for truncate columns). */
  tableClassName?: string;
}

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading,
  isFetching,
  error,
  onRetry,
  emptyTitle = "No results",
  emptyShape,
  emptyDescription,
  emptyAction,
  pagination,
  onPageChange,
  onLimitChange,
  sortBy,
  sortDir,
  onSortChange,
  sortableColumns = [],
  onRowClick,
  rowKey,
  groups,
  groupItemLabel = "items",
  groupRowsInitial = 25,
  groupRowsLoadMore = 25,
  expandAllGroups = false,
  onGroupExpandChange,
  expandedGroupKeys,
  onGroupLoadMore,
  groupNestedStyle = "plain",
  getTimelineMarkerClassName,
  renderTimelineMarkerTrailing,
  getRowTooltip,
  variant = "card",
  tableClassName,
}: DataTableProps<TData, TValue>) {
  const useTimeline = groupNestedStyle === "timeline";
  const isCell = variant === "cell";
  const timelineHasTrailing = Boolean(renderTimelineMarkerTrailing);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  const [ownExpandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const controlledKeysSig = expandedGroupKeys?.join("|");
  const expandedGroups = useMemo(
    () => (expandedGroupKeys ? new Set(expandedGroupKeys) : ownExpandedGroups),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controlledKeysSig, ownExpandedGroups],
  );
  const [visibleRowCounts, setVisibleRowCounts] = useState<Map<string, number>>(
    new Map(),
  );

  // Reset when the SET of groups changes, not on every new `groups` array:
  // a server-backed group gets new rows as they load and must stay open.
  const groupKeysSig = groups?.map((g) => g.key).join("|") ?? "";
  useEffect(() => {
    if (!expandAllGroups) {
      setExpandedGroups(new Set());
    }
    setVisibleRowCounts(new Map());
  }, [groupKeysSig, expandAllGroups]);

  const showError = !isLoading && error != null;
  const useGroups = Boolean(groups?.length);
  // Server-backed groups can be listed before any of their rows load.
  const showEmpty = !isLoading && !error && data.length === 0 && !useGroups;

  const toggleGroup = (key: string) => {
    const opening = !expandedGroups.has(key);
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    onGroupExpandChange?.(key, opening);
  };

  const loadMoreGroupRows = (key: string, total: number) => {
    setVisibleRowCounts((prev) => {
      const next = new Map(prev);
      const current = next.get(key) ?? groupRowsInitial;
      next.set(key, Math.min(current + groupRowsLoadMore, total));
      return next;
    });
  };

  const renderDataRow = (
    rowData: TData,
    nested?: {
      depth: number;
      position: TimelinePosition;
      closesGroup?: boolean;
    },
  ) => {
    const row = table
      .getRowModel()
      .rows.find((r) => rowKey(r.original) === rowKey(rowData));
    if (!row) return null;

    const timelineDepth = nested ? nested.depth + 1 : 0;
    const isNestedTimeline = useTimeline && nested;
    const tip = getRowTooltip?.(row.original);

    const tr = (
      <tr
        className={cn(
          "transition-colors duration-150",
          isNestedTimeline
            ? "group/trow bg-muted/[0.04] hover:bg-muted/50"
            : "hover:bg-muted/40",
          // No divider between rows (DES 30-09-2026: "remove dividers from
          // these type of lists"); hover still marks the row.
          onRowClick && "cursor-pointer",
          isFetching && "opacity-60",
        )}
        onClick={onRowClick ? () => onRowClick(row.original) : undefined}
      >
        {row.getVisibleCells().map((cell, cellIndex) => (
          <td
            key={cell.id}
            className={cn(
              "px-4 py-3 align-middle",
              columnClassName(cell.column.columnDef),
              useTimeline && nested && cellIndex === 0 && "relative",
            )}
            style={
              useTimeline && nested && cellIndex === 0
                ? {
                    paddingLeft: timelineContentPad(
                      timelineDepth,
                      timelineHasTrailing,
                    ),
                  }
                : undefined
            }
          >
            {useTimeline && nested && cellIndex === 0 ? (
              <GroupTimelineRail
                depth={timelineDepth}
                position={nested.position}
                markerClassName={getTimelineMarkerClassName?.(row.original)}
                markerTrailing={renderTimelineMarkerTrailing?.(row.original)}
              />
            ) : null}
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    );

    return wrapDataRowTooltip(rowKey(row.original), tip, tr);
  };

  /**
   * The column header cells. In a grouped table they sit inside each open
   * group, over its rows, not once at the top (DES 01-10-2026: "expanding
   * list per hi show hoon top per nahin").
   */
  const renderHeaderCells = (
    headerGroup: ReturnType<typeof table.getHeaderGroups>[number],
    /** Inside a timeline group: the first column's left pad, the same as
     *  its data cells, so the heading sits over the values (DES 01-10-2026:
     *  "balanced nahin hai, session ko daikho"). */
    firstPad?: number,
  ) => (
    <>
      {headerGroup.headers.map((header, headerIndex) => {
      const id = header.column.id;
      const isSortable = sortableColumns.includes(id);
      const isActive = sortBy === id;

      return (
        <th
          key={header.id}
          className={cn(
            "h-11 whitespace-nowrap px-4 text-left align-middle text-xs font-medium uppercase tracking-wider text-muted-foreground",
            isSortable && "cursor-pointer select-none hover:text-foreground",
            columnClassName(header.column.columnDef),
          )}
          style={headerIndex === 0 && firstPad !== undefined ? { paddingLeft: firstPad } : undefined}
          onClick={() => isSortable && handleSort(id)}
        >
          <span className="inline-flex items-center gap-1.5">
            {header.isPlaceholder
              ? null
              : flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
            {isSortable ? (
              isActive ? (
                sortDir === "asc" ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )
              ) : (
                <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
              )
            ) : null}
          </span>
        </th>
      );
    })}
    </>
  );

  const renderGroupSection = (
    group: TableRowGroup<TData>,
    depth = 0,
  ): ReactNode[] => {
    const isExpanded = expandAllGroups || expandedGroups.has(group.key);
    const childCount =
      group.total ??
      group.children?.reduce((sum, child) => sum + child.rows.length, 0) ??
      group.rows.length;

    const headerRow = (
      <tr
        key={`group-${group.key}`}
        className={cn(
          // No dividers between group rows (DES 01-10-2026: "lists main say
          // divider lines remove karo"); the row fill separates them.
          "border-b-0 transition-colors duration-150",
          useTimeline
            ? depth === 0
              ? "bg-muted/45 hover:bg-muted/60"
              : "bg-muted/25 hover:bg-muted/45"
            : "bg-muted/30 hover:bg-muted/45",
        )}
      >
        <td colSpan={columns.length} className="relative px-4 py-2.5">
          <button
            type="button"
            className={cn(
              "flex w-full items-center gap-3 rounded-md text-left transition-colors duration-150",
              useTimeline && "hover:bg-black/[0.03] dark:hover:bg-white/[0.04]",
            )}
            style={{
              paddingLeft: useTimeline
                ? timelineContentPad(depth) - 20
                : depth * 20,
            }}
            onClick={() => toggleGroup(group.key)}
            aria-expanded={isExpanded}
          >
            <ChevronRight
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                isExpanded && "rotate-90",
              )}
            />
            <Avatar
              src={group.avatarSrc}
              gender={group.avatarGender}
              initials={group.avatarInitials ?? "?"}
              alt=""
              size="sm"
              tone="brand"
            />
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">
              {group.label}
            </span>
            <Badge variant="outline" className="shrink-0 tabular-nums">
              {childCount}{" "}
              {childCount === 1
                ? groupItemLabel.replace(/s$/, "")
                : groupItemLabel}
            </Badge>
          </button>
        </td>
      </tr>
    );

    if (!isExpanded) {
      return [headerRow];
    }

    if (group.children?.length) {
      return [
        headerRow,
        ...group.children.flatMap((child) => renderGroupSection(child, depth + 1)),
      ];
    }

    // Server-backed: every loaded row shows; the rest come from the caller.
    const serverBacked = group.total !== undefined;
    const visibleLimit = serverBacked
      ? group.rows.length
      : (visibleRowCounts.get(group.key) ?? groupRowsInitial);
    const visibleRows = group.rows.slice(0, visibleLimit);
    const remaining = serverBacked
      ? Math.max(0, (group.total ?? 0) - group.rows.length)
      : group.rows.length - visibleRows.length;

    if (serverBacked && group.loading && group.rows.length === 0) {
      return [
        headerRow,
        ...table.getHeaderGroups().map((headerGroup) => (
          <tr key={`cols-${group.key}-${headerGroup.id}`}>
            {renderHeaderCells(
              headerGroup,
              useTimeline ? timelineContentPad(depth + 1, timelineHasTrailing) : undefined,
            )}
          </tr>
        )),
        <tr key={`loading-${group.key}`}>
          {columns.map((_col, cIdx) => (
            <td key={cIdx} className="p-4">
              <Skeleton className="h-4 w-3/4" />
            </td>
          ))}
        </tr>,
      ];
    }

    // A failed fetch says so, never "Showing 0 of N" + "Load N more" (W33).
    const footer = groupFooterKind(group, remaining);
    const loadMoreRow = footer === "error" ? (
      <GroupErrorRow
        key={`error-${group.key}`}
        colSpan={columns.length}
        itemLabel={groupItemLabel}
        onRetry={group.onRetry}
      />
    ) : footer === "load-more" ? (
        <GroupLoadMoreRow
          key={`load-more-${group.key}`}
          colSpan={columns.length}
          shown={visibleRows.length}
          total={serverBacked ? (group.total ?? 0) : group.rows.length}
          batchSize={Math.min(groupRowsLoadMore, remaining)}
          itemLabel={groupItemLabel}
          onLoadMore={() =>
            serverBacked
              ? onGroupLoadMore?.(group.key)
              : loadMoreGroupRows(group.key, group.rows.length)
          }
        />
      ) : null;

    const firstPad = useTimeline
      ? timelineContentPad(depth + 1, timelineHasTrailing)
      : undefined;
    const columnsRow = table.getHeaderGroups().map((headerGroup) => (
      <tr key={`cols-${group.key}-${headerGroup.id}`}>
        {renderHeaderCells(headerGroup, firstPad)}
      </tr>
    ));

    return [
      headerRow,
      ...columnsRow,
      ...visibleRows
        .map((rowData, index) =>
          renderDataRow(rowData, {
            depth,
            position: timelinePosition(index, visibleRows.length),
            closesGroup: index === visibleRows.length - 1 && remaining === 0,
          }),
        )
        .filter(Boolean),
      ...(loadMoreRow ? [loadMoreRow] : []),
    ];
  };

  // ----------------------------------------------------------------------
  // Sortable header click handler
  // ----------------------------------------------------------------------
  const handleSort = (columnId: string) => {
    if (!onSortChange || !sortableColumns.includes(columnId)) return;
    if (sortBy === columnId) {
      onSortChange(columnId, sortDir === "asc" ? "desc" : "asc");
    } else {
      onSortChange(columnId, "desc");
    }
  };

  return (
    <TooltipProvider delayDuration={280}>
    <div
      className={cn(
        "min-w-0 max-w-full",
        !isCell && "space-y-3",
        isCell && "w-full",
      )}
    >
      <div
        className={cn(
          "min-w-0 max-w-full",
          isCell
            ? "w-full overflow-x-auto"
            : "overflow-hidden rounded-lg border bg-card",
        )}
      >
        <div
          className={cn(
            "min-w-0 max-w-full",
            isCell ? "w-full" : "relative w-full overflow-x-auto",
          )}
        >
          <table
            className={cn("w-full caption-bottom text-sm", tableClassName)}
          >
            {/* ----------------------------------------------------------------------
                Header
                ---------------------------------------------------------------------- */}
            <thead
              className={cn(
                !useGroups && "[&_tr]:border-b",
                !isCell && !useGroups && "bg-muted/40",
              )}
            >
              {useGroups
                ? null
                : table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="border-b transition-colors">
                      {renderHeaderCells(headerGroup)}
                    </tr>
                  ))}
            </thead>

            {/* ----------------------------------------------------------------------
                Body — loading / error / empty / data
                ---------------------------------------------------------------------- */}
            <tbody className="[&_tr:last-child]:border-0">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`}>
                    {columns.map((_col, cIdx) => (
                      <td key={cIdx} className="p-4">
                        <Skeleton className="h-4 w-3/4" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : showError ? (
                <tr>
                  <td colSpan={columns.length} className="p-4">
                    <ErrorState error={error} onRetry={onRetry} />
                  </td>
                </tr>
              ) : showEmpty ? (
                <tr>
                  <td colSpan={columns.length} className="p-4">
                    <EmptyState
                      title={emptyTitle}
                      description={emptyDescription}
                      action={emptyAction}
                      shape={emptyShape}
                    />
                  </td>
                </tr>
              ) : useGroups ? (
                groups!.flatMap((group) => renderGroupSection(group))
              ) : (
                table.getRowModel().rows.map((row) => {
                  const tip = getRowTooltip?.(row.original);
                  const tr = (
                    <tr
                      className={cn(
                        "transition-colors hover:bg-muted/40",
                        onRowClick && "cursor-pointer",
                        isFetching && "opacity-60",
                      )}
                      onClick={
                        onRowClick
                          ? () => onRowClick(row.original)
                          : undefined
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            "px-4 py-3 align-middle",
                            columnClassName(cell.column.columnDef),
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                  return wrapDataRowTooltip(rowKey(row.original), tip, tr);
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ----------------------------------------------------------------------
          Pagination footer
          ---------------------------------------------------------------------- */}
      {pagination && pagination.total > 0 ? (
        <div
          className={cn(
            isCell && "border-t border-border/60 px-4 py-3 md:px-5",
          )}
        >
          <PaginationFooter
            pagination={pagination}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      ) : null}
    </div>
    </TooltipProvider>
  );
}

/** Attach an optional hover tooltip to a data `<tr>` without breaking table DOM. */
function wrapDataRowTooltip(
  key: string,
  tip: ReactNode | undefined,
  tr: ReactElement,
): ReactNode {
  if (!tip) {
    return cloneElement(tr, { key });
  }
  return (
    <Tooltip key={key}>
      <TooltipTrigger asChild>{tr}</TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-xs space-y-1 text-left font-normal"
      >
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

// ----------------------------------------------------------------------
// Timeline rail — vertical spine + node + branch to row content
// ----------------------------------------------------------------------
function GroupTimelineRail({
  depth,
  position,
  markerClassName,
  markerTrailing,
}: {
  depth: number;
  position: TimelinePosition;
  markerClassName?: string;
  markerTrailing?: ReactNode;
}) {
  const left = timelineRailLeft(depth);
  // Branch long enough to clear status node then seat the trailing badge.
  const branchWidth = markerTrailing ? 34 : 14;

  return (
    <div
      className="pointer-events-none absolute inset-y-0"
      style={{ left }}
      aria-hidden={!markerTrailing}
    >
      {position === "middle" || position === "last" || position === "only" ? (
        <div className="absolute left-0 top-0 h-1/2 w-px bg-border/80" />
      ) : null}
      {position === "first" || position === "middle" ? (
        <div className="absolute bottom-0 left-0 h-1/2 w-px bg-border/80" />
      ) : null}

      {/* Horizontal stub toward content */}
      <div
        className="absolute top-1/2 h-px bg-border/80"
        style={{ left: 0, width: branchWidth }}
      />

      {/* Status node — always centered on the spine */}
      <div
        className={cn(
          "absolute top-1/2 left-0 z-[1] h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-background shadow-sm transition-[transform,background-color,border-color,box-shadow] duration-150 group-hover/trow:scale-110 group-hover/trow:shadow-md",
          markerClassName ??
            "border-primary/40 group-hover/trow:border-primary/70 group-hover/trow:bg-primary/10",
        )}
      />

      {/* Trailing badge sits on the stub, past the node — never on the spine */}
      {markerTrailing ? (
        <span
          className="pointer-events-auto absolute top-1/2 z-[2] -translate-y-1/2"
          style={{ left: 10 }}
        >
          {markerTrailing}
        </span>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------------
// Group load-more footer (nested pagination within a leaf group)
// ----------------------------------------------------------------------
function GroupLoadMoreRow({
  colSpan,
  shown,
  total,
  batchSize,
  itemLabel,
  onLoadMore,
}: {
  colSpan: number;
  shown: number;
  total: number;
  batchSize: number;
  itemLabel: string;
  onLoadMore: () => void;
}) {
  const progress = total > 0 ? Math.round((shown / total) * 100) : 0;

  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        <div className="flex justify-end bg-muted/15 px-4 py-3">
          <div className="flex w-full max-w-sm flex-col items-end gap-2.5">
            <div className="flex w-full items-center justify-end gap-3">
              <p className="text-right text-xs text-muted-foreground">
                Showing{" "}
                <span className="font-medium tabular-nums text-foreground">
                  {shown.toLocaleString()}
                </span>{" "}
                of{" "}
                <span className="font-medium tabular-nums text-foreground">
                  {total.toLocaleString()}
                </span>{" "}
                {total === 1 ? itemLabel.replace(/s$/, "") : itemLabel}
              </p>
              <span className="shrink-0 text-[11px] font-medium tabular-nums text-muted-foreground">
                {progress}%
              </span>
            </div>
            <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-brand/70 transition-[width] duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 rounded-full px-4 text-xs font-medium"
              onClick={onLoadMore}
            >
              <ChevronDown className="h-3.5 w-3.5" />
              Load {batchSize.toLocaleString()} more
            </Button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ----------------------------------------------------------------------
// Group error footer (a server-backed group whose fetch failed, W33)
// ----------------------------------------------------------------------
function GroupErrorRow({
  colSpan,
  itemLabel,
  onRetry,
}: {
  colSpan: number;
  itemLabel: string;
  onRetry?: () => void;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        <div
          role="alert"
          className="flex flex-wrap items-center justify-start gap-3 bg-muted/15 px-4 py-3 sm:justify-end"
        >
          {/* Left on a phone: the table scrolls sideways there (W33). */}
          <p className="text-xs text-muted-foreground sm:text-right">
            Could not load these {itemLabel}. Try again
          </p>
          {onRetry ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 rounded-full px-4 text-xs font-medium"
              onClick={onRetry}
            >
              <RotateCw className="h-3.5 w-3.5" />
              Retry
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

// ----------------------------------------------------------------------
// Pagination footer (kept private to this file — not generally reusable)
// ----------------------------------------------------------------------
function PaginationFooter({
  pagination,
  onPageChange,
  onLimitChange,
}: {
  pagination: PaginationState;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}) {
  const { page, limit, total, totalPages } = pagination;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(total, page * limit);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="flex flex-col gap-3 px-1 sm:flex-row sm:items-center sm:justify-between">
      <p className="shrink-0 text-xs text-muted-foreground">
        Showing <span className="font-medium text-foreground">{start}</span>–
        <span className="font-medium text-foreground">{end}</span> of{" "}
        <span className="font-medium text-foreground">{total.toLocaleString()}</span>
      </p>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {onLimitChange ? (
          <Select
            value={String(limit)}
            onValueChange={(v) => onLimitChange(Number(v))}
          >
            <SelectTrigger className="h-9 w-auto shrink-0 min-w-[124px] rounded-[1.05rem] px-4 text-sm font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n} / page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}

        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canPrev}
            onClick={() => canPrev && onPageChange?.(page - 1)}
          >
            Previous
          </Button>
          <span className="whitespace-nowrap px-1 text-xs text-muted-foreground">
            Page <span className="font-medium text-foreground">{page}</span> of{" "}
            {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canNext}
            onClick={() => canNext && onPageChange?.(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
